import { useEffect, useRef, useState } from 'react';
import type { DestinationContentSchema } from 'typings/destinations/DestinationContentSchema';
import type { DestinationContentTypes } from 'typings/destinations/DestinationContentTypeEnum';
import { useDestinationEditor } from './DestinationEditorContext';

type MapItems = (items: DestinationContentSchema[]) => DestinationContentSchema[];

const identity: MapItems = (items) => items;

/**
 * Local editable copy of destination contents for a given content type,
 * re-synced from the destination context whenever content is replaced
 * externally (AI generation/translation). This ensures newly added
 * translations become visible when switching language without a full
 * page refresh.
 */
export function useSyncedDestinationContents(contentType: DestinationContentTypes, mapItems: MapItems = identity) {
    const { destination, contentSyncKey } = useDestinationEditor();

    const [items, setItems] = useState(() =>
        mapItems(destination?.contents?.filter((content) => content.content_type === contentType) ?? [])
    );

    // Keep latest contents/mapper in refs so the effect can depend only on
    // contentSyncKey (and contentType) without re-running on every render.
    const destinationContentsRef = useRef(destination?.contents);
    destinationContentsRef.current = destination?.contents;
    const mapItemsRef = useRef(mapItems);
    mapItemsRef.current = mapItems;
    const isFirstSyncRef = useRef(true);

    useEffect(() => {
        // Skip the initial render — useState already initialized from contents.
        if (isFirstSyncRef.current) {
            isFirstSyncRef.current = false;
            return;
        }
        setItems(
            mapItemsRef.current(
                destinationContentsRef.current?.filter((content) => content.content_type === contentType) ?? []
            )
        );
    }, [contentSyncKey, contentType]);

    return [items, setItems] as const;
}
